import type { DiaperPayload, EventType, FormulaPayload, LocalEvent, SleepPayload, SyncState } from '@/lib/schemas';
import { formatDuration } from '@/lib/time';
import { IconAlert, IconBottle, IconCheck, IconCloud, IconDevice, IconDrop, IconMoon, IconSync } from './Icons';

export const TYPE_META: Record<EventType, { label: string; color: string; deep: string; Icon: typeof IconBottle }> = {
  formula: { label: 'Feed', color: 'var(--color-feed)', deep: 'var(--color-feed-deep)', Icon: IconBottle },
  sleep: { label: 'Sleep', color: 'var(--color-sleep)', deep: 'var(--color-sleep-deep)', Icon: IconMoon },
  diaper: { label: 'Diaper', color: 'var(--color-diaper)', deep: 'var(--color-diaper-deep)', Icon: IconDrop },
};

export function eventTitle(e: LocalEvent, nowMs = Date.now()): string {
  switch (e.type) {
    case 'formula':
      return `${formatMl((e.payload as FormulaPayload).amount_ml)} formula`;
    case 'sleep': {
      const p = e.payload as SleepPayload;
      const kind = p.sleep_kind === 'nap' ? 'Nap' : p.sleep_kind === 'night' ? 'Night sleep' : 'Sleep';
      if (e.ended_ms === null) return `${kind} · running ${formatDuration(nowMs - e.started_ms)}`;
      return `${kind} · ${formatDuration(e.ended_ms - e.started_ms)}`;
    }
    case 'diaper': {
      const s = (e.payload as DiaperPayload).status;
      return s === 'wet' ? 'Wet diaper' : s === 'dirty' ? 'Dirty diaper' : 'Wet + dirty diaper';
    }
  }
}

export function formatMl(ml: number): string {
  return `${Math.round(ml * 10) / 10} mL`;
}

export function TypeIcon({ type, size = 40 }: { type: EventType; size?: number }) {
  const m = TYPE_META[type];
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-2xl"
      style={{
        width: size,
        height: size,
        color: m.color,
        background: `color-mix(in oklab, ${m.color} 16%, transparent)`,
        boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${m.color} 25%, transparent)`,
      }}
    >
      <m.Icon size={Math.round(size * 0.52)} />
    </span>
  );
}

const SYNC_META: Record<SyncState, { label: string; cls: string; Icon: typeof IconCheck }> = {
  local: { label: 'Saved on this device', cls: 'text-ink-400', Icon: IconDevice },
  syncing: { label: 'Syncing', cls: 'text-sleep', Icon: IconSync },
  synced: { label: 'Synced', cls: 'text-ok/80', Icon: IconCloud },
  conflict: { label: 'Conflict — needs review', cls: 'text-warn', Icon: IconAlert },
  error: { label: 'Sync error', cls: 'text-danger', Icon: IconAlert },
};

export function SyncBadge({ state, showLabel = false }: { state: SyncState; showLabel?: boolean }) {
  const m = SYNC_META[state];
  return (
    <span className={`inline-flex items-center gap-1 text-[0.7rem] ${m.cls}`} title={m.label}>
      <m.Icon size={14} className={state === 'syncing' ? 'animate-spin [animation-duration:2s]' : ''} />
      {showLabel ? <span>{m.label}</span> : <span className="sr-only">{m.label}</span>}
    </span>
  );
}
